import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { compose, flexbox, space } from 'styled-system';

const baseDropdownItemStyles = ({ theme }) => css`
  align-items: center;
  color: ${theme.colors.dropdown.foreground};
  cursor: pointer;
  display: flex;
  font-size: ${theme.typography.texts.size2.fontSize};
  line-height: ${theme.typography.texts.size2.lineHeight};
  padding: ${theme.space[2]} ${theme.space[4]};
  user-select: none;
  white-space: nowrap;

  &:hover {
    background: ${theme.colors.dropdown.itemHover};
  }

  ${theme.mediaQueries.lg} {
    font-size: ${theme.typography.texts.size1.fontSize};
    line-height: ${theme.typography.texts.size1.lineHeight};
  }

  * {
    cursor: pointer;
  }
`;

const dividerDropdownItemStyles = ({ theme, divider }) =>
  divider &&
  css`
    border-top: 0.0625rem solid ${theme.colors.dropdown.divider};
  `;

const StyledDropdownItem = styled('div')`
  ${baseDropdownItemStyles}
  ${dividerDropdownItemStyles}
  ${compose(flexbox, space)}
`;

const DropdownItem = forwardRef(({ ariaLabel, divider, ...rest }, ref) => (
  <StyledDropdownItem
    role="menuitem"
    divider={divider}
    ref={ref}
    aria-label={ariaLabel}
    {...rest}
  />
));

DropdownItem.defaultProps = {
  ariaLabel: null,
  divider: false,
  onClick: () => {},
};

DropdownItem.displayName = 'DropdownItem';

DropdownItem.propTypes = {
  /**
   * `aria-label` to be used
   */
  ariaLabel: PropTypes.string,
  /**
   * List text to be rendered as children
   */
  children: PropTypes.node.isRequired,
  /**
   * Whether item uses a divider
   */
  divider: PropTypes.bool,
  /**
   * Callback function on click
   */
  onClick: PropTypes.func,
  /**
   * Implements `styled-system` [flexbox](https://styled-system.com/api/#flexbox) utility
   */
  ...propTypes.flexbox,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
};

export default DropdownItem;
